import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Eraser, X, Trash2 } from "lucide-react";

export const ClearDemoDataPanel = () => {
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const open = async () => {
    try {
      const { data } = await api.get("/admin/demo-data/preview");
      setPreview(data); setConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load preview"); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-data/clear", { confirm });
      toast.success(`Removed ${data.total} test item(s)`);
      setPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="clear-demo-panel" className="mt-8 rounded-xl border border-zinc-200 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Before going live</div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">Clear Demo Data</h2>
          <p className="mt-1 text-sm text-zinc-600">Removes test logins and anything named "TEST_…". Your real projects, leads and clients stay.</p>
        </div>
        <button data-testid="clear-demo-open" onClick={open} className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
          <Eraser size={14} /> Review & clear
        </button>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/25 p-4 backdrop-blur-sm" onClick={() => setPreview(null)}>
          <div data-testid="clear-demo-modal" className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-zinc-200 bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-red-600">Clear demo data</div>
                <h3 data-testid="clear-demo-total" className="mt-1 text-lg font-semibold text-zinc-900">{preview.total} item(s) will be deleted</h3>
              </div>
              <button data-testid="clear-demo-close" onClick={() => setPreview(null)} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
            </div>

            {preview.total === 0 ? (
              <p data-testid="clear-demo-empty" className="mt-4 text-sm text-zinc-600">Nothing to clear. Your data is already clean.</p>
            ) : (
              <>
                <div className="mt-4 space-y-3">
                  {preview.groups.filter((g) => g.count > 0).map((g) => (
                    <div key={g.key} data-testid={`clear-demo-group-${g.key}`} className="rounded-lg border border-zinc-200 p-3">
                      <div className="flex items-center justify-between text-sm font-medium text-zinc-900">
                        <span>{g.label}</span><span className="text-zinc-500">{g.count}</span>
                      </div>
                      <ul className="mt-1.5 space-y-0.5 text-xs text-zinc-600">
                        {g.samples.map((s, i) => <li key={i} className="truncate">• {s}</li>)}
                        {g.count > g.samples.length && <li className="italic text-zinc-400">+ {g.count - g.samples.length} more…</li>}
                      </ul>
                    </div>
                  ))}
                </div>
                <label className="mt-5 block text-xs font-medium text-zinc-600">Type <strong className="text-zinc-900">DELETE</strong> to confirm</label>
                <input data-testid="clear-demo-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20" />
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => setPreview(null)} className="rounded-md px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
                  <button data-testid="clear-demo-confirm-btn" disabled={confirm !== "DELETE" || busy} onClick={clear} className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40">
                    <Trash2 size={14} /> {busy ? "Deleting…" : "Delete permanently"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
